import React from 'react';
import { Link } from 'react-router-dom';
import { founderData, teamMembers, organizationData } from '../mock';
import { Button } from '../components/ui/button';
import { Target, Award, Heart, Users } from 'lucide-react';
import { Card } from '../components/ui/card';

const About = () => {
  const coreValues = [
    {
      icon: Target,
      title: "Mastery",
      description: "We believe in the pursuit of excellence through continuous learning and skill development."
    },
    {
      icon: Award,
      title: "Opportunity",
      description: "We create pathways for veterans and military families to build sustainable careers."
    },
    {
      icon: Heart,
      title: "Service",
      description: "Our commitment to the military community drives everything we do."
    }
  ];

  return (
    <div className="bg-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <h1 className="font-black text-5xl md:text-8xl leading-[0.76] text-[#0A277A] uppercase mb-8">
            About MOS Veteran
          </h1>
          <p className="text-gray-800 text-xl md:text-2xl max-w-3xl leading-relaxed">
            From grassroots veteran support to comprehensive empowerment services—our journey is built on service, skill, and self-mastery.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="max-w-4xl mx-auto">
            <div>
              <h2 className="font-black text-3xl md:text-5xl text-[#0A277A] uppercase mb-8">
                About MOS Veteran (Mastery of Self)
              </h2>
              
              <div className="bg-white p-8 md:p-10 rounded-lg shadow-lg border-l-4 border-[#D80000] mb-8">
                <h3 className="font-black text-2xl md:text-3xl text-[#0A277A] uppercase mb-6 text-center">
                  M.O.S. — Mastery · Opportunity · Service
                </h3>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  At MOS Veteran, these three pillars define everything we stand for — and together, they embody the true meaning of <span className="font-bold text-[#0A277A]">Mastery of Self</span>.
                </p>
              </div>

              <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
                <p>
                  We believe that mastering yourself is the first step toward mastering your future. That's why MOS was created — to give veterans, spouses, and dependents the tools, mindset, and opportunities to transform their military discipline into lifelong success.
                </p>
                
                <p>
                  What began as a grassroots effort to connect and support veterans has grown into a powerful movement focused on empowerment and purpose. Built on the values of <span className="font-bold text-[#0A277A]">Mastery</span>, <span className="font-bold text-[#D80000]">Opportunity</span>, and <span className="font-bold text-[#3F54C8]">Service</span>, MOS bridges the gap between military life and civilian success — helping every member of our community rise with confidence, direction, and skill.
                </p>
                
                <div className="bg-[#0A277A] text-white p-6 rounded-lg my-6">
                  <p className="font-semibold text-lg">
                    Launching in 2026, <span className="text-[#D80000] font-black">MOS Academy</span> will take that mission even further with hands-on vocational training and mentorship programs — starting with our flagship Barber Training Program. Through these initiatives, we're not just teaching trades — we're creating pathways for financial independence, entrepreneurship, and generational impact.
                  </p>
                </div>
                
                <p className="text-xl font-semibold text-[#0A277A] italic">
                  At MOS Veteran, our mission is simple: to help veterans build businesses, careers, and legacies that reflect the discipline and resilience forged through service — and to remind every member of our community that the greatest mission begins within.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-6xl text-[#0A277A] uppercase mb-4">
              Our Core Values
            </h2>
            <p className="text-gray-600 text-lg">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coreValues.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="bg-gray-50 border border-gray-200 p-8 text-center hover:border-[#D80000] transition-all duration-300">
                  <Icon className="mx-auto mb-6 text-[#0A277A]" size={48} />
                  <h3 className="font-black text-2xl text-[#0A277A] uppercase mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-800 leading-relaxed">
                    {value.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-6xl text-[#0A277A] uppercase mb-4">
              Meet the Mastery of Self® Leadership Team
            </h2>
          </div>

          {/* Bridget Garcia-Davis */}
          <div className="max-w-5xl mx-auto mb-20">
            <Card className="bg-white border-2 border-[#D80000] overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="mb-6">
                  <h3 className="font-black text-3xl text-[#0A277A] uppercase mb-2">
                    Bridget Garcia-Davis
                  </h3>
                  <p className="text-[#D80000] font-bold text-lg uppercase tracking-wide">
                    President & Co-Founder, Mastery of Self®
                  </p>
                </div>
                
                <div className="bg-[#0A277A] text-white p-6 rounded-lg mb-6">
                  <p className="text-lg italic leading-relaxed">
                    "Before we can master the world around us, we must first master what's within us."
                  </p>
                </div>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Bridget Garcia-Davis brings over a decade of experience in business, military service, and personal transformation to her role as President & Co-Founder of Mastery of Self® (M.O.S.). A U.S. Army Veteran and seasoned entrepreneur, she holds a Master's degree in Business Management with a concentration in Accounting.
                  </p>
                  <p>
                    Her mission is clear: to equip veterans and their families with the mindset, emotional clarity, and financial literacy needed to reclaim their power and rewrite their story.
                  </p>
                  <p>
                    As the architect of the <span className="font-bold text-[#0A277A]">Read to Reclaim®</span> initiative, Bridget leads M.O.S.'s education and personal development programs—curating transformational book kits, guided reflection tools, and summits designed to ignite internal growth.
                  </p>
                  <p className="font-semibold text-[#0A277A]">
                    Under her leadership, Mastery of Self® is becoming more than a nonprofit—it's a movement. A safe space where military-connected individuals are reminded that their story doesn't end with transition; it evolves. Through books, conversations, and bold action, Bridget is building a legacy rooted in self-mastery and lasting change.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Torey D. Crawford */}
          <div className="max-w-5xl mx-auto mb-20">
            <Card className="bg-white border-2 border-[#3F54C8] overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="mb-6">
                  <h3 className="font-black text-3xl text-[#0A277A] uppercase mb-2">
                    Torey D. Crawford
                  </h3>
                  <p className="text-[#3F54C8] font-bold text-lg uppercase tracking-wide">
                    Vice President & Co-Founder, Mastery of Self®
                  </p>
                </div>
                
                <div className="bg-[#3F54C8] text-white p-6 rounded-lg mb-6">
                  <p className="text-lg italic leading-relaxed">
                    "True mastery begins when we choose purpose over comfort and growth over fear."
                  </p>
                </div>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Torey D. Crawford serves as the Vice President & Co-Founder of Mastery of Self® (M.O.S.), a nonprofit organization dedicated to empowering veterans, military spouses, and dependents to build purpose-driven lives through skill mastery, personal growth, and community connection.
                  </p>
                  <p>
                    A U.S. Army Veteran with over 18 years of honorable service, Torey combines strategic leadership with a passion for business development and human potential. He holds a Bachelor's degree in Business and brings extensive experience in military logistics, organizational management, and operations strategy—skills that allow him to design programs transforming vision into measurable impact.
                  </p>
                  <p>
                    Under his leadership, M.O.S. has evolved into a holistic empowerment ecosystem—launching initiatives such as <span className="font-bold text-[#D80000]">M.O.S. Academy</span>, a vocational training and barbershop education program, and <span className="font-bold text-[#0A277A]">Read to Reclaim®</span>, a literacy and mindset development platform.
                  </p>
                  <p className="font-semibold text-[#0A277A]">
                    Torey's leadership is grounded in purpose and strategy. He is driven by the belief that true transformation begins within—and through mentorship, education, and opportunity, he continues to build a legacy that empowers individuals to rise, lead, and master the art of self.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Shannin Garcia-Davis */}
          <div className="max-w-5xl mx-auto mb-20">
            <Card className="bg-white border-2 border-[#0A277A] overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="mb-6">
                  <h3 className="font-black text-3xl text-[#0A277A] uppercase mb-2">
                    Shannin Garcia-Davis
                  </h3>
                  <p className="text-[#0A277A] font-bold text-lg uppercase tracking-wide">
                    Treasurer & Co-Founder, Mastery of Self®
                  </p>
                </div>
                
                <div className="bg-[#0A277A] text-white p-6 rounded-lg mb-6">
                  <p className="text-lg italic leading-relaxed">
                    "Structure creates the space where self-mastery can grow."
                  </p>
                </div>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Shannin Garcia-Davis serves as the Treasurer & Co-Founder of Mastery of Self® (M.O.S.), where she brings over a decade of business leadership and 18 years of military service into focus for a powerful mission: helping veterans and their families reclaim their purpose through education, structure, and self-mastery.
                  </p>
                  <p>
                    A seasoned logistics professional, Shannin currently serves in government as a logistical manager—translating mission-critical coordination into real-world impact. Her operations background allows her to navigate complex systems and ensure every aspect of the M.O.S. mission flows with intention and efficiency.
                  </p>
                  <p>
                    Shannin holds a Bachelor's degree in Business with a concentration in Small Business and Entrepreneurship, and she combines tactical planning with heart-centered leadership. As the steward of M.O.S.'s financial health and logistics, she ensures resources are maximized and programs are executed with precision.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* LaShawnda Crawford */}
          <div className="max-w-5xl mx-auto">
            <Card className="bg-white border-2 border-[#D80000] overflow-hidden">
              <div className="p-8 md:p-12">
                <div className="mb-6">
                  <h3 className="font-black text-3xl text-[#0A277A] uppercase mb-2">
                    LaShawnda Crawford
                  </h3>
                  <p className="text-[#D80000] font-bold text-lg uppercase tracking-wide">
                    Secretary & Co-Founder, Mastery of Self®
                  </p>
                </div>
                
                <div className="bg-[#D80000] text-white p-6 rounded-lg mb-6">
                  <p className="text-lg italic leading-relaxed">
                    "Service is more than a duty—it's a way of life."
                  </p>
                </div>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    LaShawnda Crawford serves as the Secretary & Co-Founder of Mastery of Self® (M.O.S.), bringing over 22 years of experience within the military community and a deep passion for service, family, and empowerment.
                  </p>
                  <p>
                    As a devoted military spouse and leader, LaShawnda has spent more than two decades supporting transitioning service members, spouses, and dependents through mentorship, resource coordination, and community engagement. Her lived experience within the military lifestyle gives her a unique ability to connect with others navigating change, uncertainty, and growth.
                  </p>
                  <p>
                    Known for her strength in organization, empathy, and leadership, LaShawnda plays a pivotal role in shaping M.O.S.'s programs and outreach efforts. She ensures that every initiative—whether through <span className="font-bold text-[#0A277A]">Read to Reclaim®</span> or <span className="font-bold text-[#D80000]">M.O.S. Academy</span>—reflects the organization's mission of self-mastery, purpose, and resilience.
                  </p>
                  <p className="font-semibold text-[#0A277A]">
                    With a steady balance of grace and grit, LaShawnda leads by example—showing that true mastery is found in service, consistency, and the courage to keep growing, no matter the season of life.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-[#0A277A] text-white">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-black text-3xl md:text-5xl uppercase mb-8">
            Our Mission
          </h2>
          <p className="text-white/95 text-xl leading-relaxed">
            To empower veterans, military spouses, and dependents to build purpose-driven lives through skill mastery, personal growth, and community connection. We believe transformation begins within—through education, opportunity, and self-leadership.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;